import { useId, useState, type FormEvent } from 'react';
import { LIMITS } from '@shared/constants';
import { errorMessage, put } from '../lib/api';
import { Dialog } from './Dialog';
import { Notice } from './Notice';

interface Props {
  open: boolean;
  /** False for Google-only accounts: they create a password instead of changing it. */
  hasPassword: boolean;
  onClose(): void;
  onChanged(): void;
}

/** Changes (or creates) the account password; other devices are signed out by the server. */
export function ChangePasswordDialog({ open, hasPassword, onClose, onChanged }: Props) {
  const formId = useId();
  const ids = { current: useId(), next: useId(), confirm: useId() };
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    if (busy) return;
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (hasPassword && !current) return setError('Informe a senha atual.');
    if (next.length < LIMITS.minPasswordLength) return setError(`A nova senha deve ter pelo menos ${LIMITS.minPasswordLength} caracteres.`);
    if (next !== confirm) return setError('As duas senhas novas não são iguais.');
    setBusy(true);
    try {
      await put('/api/user/password', { ...(hasPassword ? { currentPassword: current } : {}), newPassword: next });
      setBusy(false);
      setCurrent('');
      setNext('');
      setConfirm('');
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      title={hasPassword ? 'Trocar senha' : 'Criar senha'}
      onClose={close}
      dismissible={!busy}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={busy}>
            {busy ? 'Salvando…' : 'Salvar senha'}
          </button>
        </>
      }
    >
      <form id={formId} className="form" onSubmit={submit} noValidate>
        {!hasPassword && (
          <p className="muted small">Sua conta usa o login com Google. Com uma senha, você também pode entrar com e-mail e senha.</p>
        )}
        {hasPassword && (
          <div className="field">
            <label htmlFor={ids.current}>Senha atual</label>
            <input id={ids.current} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
        )}
        <div className="field">
          <label htmlFor={ids.next}>Nova senha</label>
          <input id={ids.next} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
          <p className="muted small">Mínimo de {LIMITS.minPasswordLength} caracteres.</p>
        </div>
        <div className="field">
          <label htmlFor={ids.confirm}>Repita a nova senha</label>
          <input id={ids.confirm} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <p className="muted small">Por segurança, a conta sai dos outros aparelhos e você entra neles de novo com a nova senha.</p>
        {error && <Notice kind="error">{error}</Notice>}
      </form>
    </Dialog>
  );
}
